import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { enviroment } from 'src/environments/environment';

type DownloadOs = 'windows' | 'macos' | 'linux';
type PageState = 'loading' | 'ready' | 'error';

interface DownloadFile {
  os: DownloadOs;
  fileName: string;
  url: string;
  sizeBytes: number;
  requirement: string;
}

interface DownloadsManifest {
  version: string;
  releasedAt: string;
  changelogUrl: string | null;
  files: DownloadFile[];
}

const CHANGELOG_TEXT_COLOR = 'var(--ftds-text)';
const UNSAFE_ELEMENTS = 'script,iframe,object,embed,link,meta,base,form,style';

const OS_ORDER: DownloadOs[] = ['windows', 'macos', 'linux'];
const FORMAT_BY_EXTENSION: Record<string, string> = {
  exe: 'downloads.fmtExe',
  msi: 'downloads.fmtMsi',
  dmg: 'downloads.fmtDmg',
  appimage: 'downloads.fmtAppImage',
  deb: 'downloads.fmtDeb',
};

@Component({
  selector: 'app-downloads',
  templateUrl: './downloads.component.html',
  styleUrls: ['./downloads.component.css'],
})
export class DownloadsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  readonly skeletonRows = [1, 2, 3];

  state: PageState = 'loading';
  manifest: DownloadsManifest | null = null;
  search = '';
  changelogOpen = false;
  changelogState: PageState = 'loading';
  changelogHtml: SafeHtml | null = null;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.state = 'loading';
    this.http
      .get<DownloadsManifest>(`${enviroment.downloadsBaseUrl}/downloads.json`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (manifest) => {
          this.manifest = manifest;
          this.state = 'ready';
          this.loadChangelog();
        },
        error: () => {
          this.manifest = null;
          this.state = 'error';
        },
      });
  }

  openChangelog(): void {
    this.changelogOpen = true;
    if (!this.changelogHtml) {
      this.loadChangelog();
    }
  }

  loadChangelog(): void {
    const url = this.manifest?.changelogUrl;
    if (!url) {
      return;
    }
    this.changelogState = 'loading';
    this.http
      .get(url, { responseType: 'text' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (html) => {
          this.changelogHtml = this.sanitizer.bypassSecurityTrustHtml(this.cleanChangelog(html));
          this.changelogState = 'ready';
        },
        error: () => {
          this.changelogState = 'error';
        },
      });
  }

  // O sanitizador padrão do Angular descarta todo atributo style, e o changelog é formatado só com style inline.
  private cleanChangelog(html: string): string {
    const doc = new DOMParser().parseFromString(
      html.split('%1').join(CHANGELOG_TEXT_COLOR),
      'text/html',
    );
    doc.querySelectorAll(UNSAFE_ELEMENTS).forEach((el) => el.remove());
    doc.body.querySelectorAll('*').forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        const value = attr.value.trim().toLowerCase();
        if (attr.name.startsWith('on') || value.startsWith('javascript:')) {
          el.removeAttribute(attr.name);
        }
      }
    });
    return doc.body.innerHTML;
  }

  get sortedFiles(): DownloadFile[] {
    const files = this.manifest?.files ?? [];
    return [...files].sort((a, b) => OS_ORDER.indexOf(a.os) - OS_ORDER.indexOf(b.os));
  }

  get visibleFiles(): DownloadFile[] {
    const term = this.search.trim().toLowerCase();
    return this.sortedFiles.filter(
      (f) => !term || `${f.fileName} ${f.os}`.toLowerCase().includes(term),
    );
  }

  formatKey(fileName: string): string | null {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return FORMAT_BY_EXTENSION[extension] ?? null;
  }

  formatSize(bytes: number): string {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
